import { useId, useState, useEffect, useRef } from 'react';

import { useT } from '../../i18n';
import { safeId } from '../ui/labelledControl';

export interface NumberPadPopupProps {
  /** Ref to the input element the popup anchors to (positions relative to its bounding box). */
  anchorRef: any;
  /** Largest number to offer (0..maxValue inclusive). */
  maxValue: number;
  /** Called with the picked number. */
  onPick: (n: number) => void;
  /** Called when the popup should close (Escape / outside click / ✕). */
  onClose: () => void;
  /**
   * Move keyboard focus INTO the pad (#943) — to the button for `value` when
   * there is one, else the first number. For a keyboard user who opened the pad
   * from its field with Enter or Space; a pointer never sets it, and the
   * existing callers do not pass it, so they behave exactly as before.
   */
  focusInside?: boolean;
  /** The field's current value, so `focusInside` lands on it. */
  value?: string | number | null;
  /**
   * Close when keyboard focus LEAVES the pad for somewhere other than its
   * field (#943), so the pad never covers the control that now has focus
   * (WCAG 2.4.11). Only a real destination counts — focus going nowhere (a tap
   * on empty space) is left to the outside-click rule, as before. Opt-in.
   */
  closeOnFocusLeave?: boolean;
}

/** Floating number-pad popup for quickly entering a score — renders 0..maxValue in a grid, positions itself under the anchored input (flips above when no room), and closes on Escape / outside click; extracted verbatim from the original artifact (legacy/App.jsx). */
export function NumberPadPopup({
  anchorRef,
  maxValue,
  onPick,
  onClose,
  focusInside = false,
  value = null,
  closeOnFocusLeave = false,
}: NumberPadPopupProps) {
  const [pos, setPos] = useState(null);
  const t = useT();
  const headingId = `${safeId('number-pad', useId())}-heading`;

  // Compute position from the anchor element. Re-run on scroll/resize
  // so the popup follows the input field if the page moves.
  useEffect(() => {
    const update = () => {
      if (!anchorRef.current) return;
      const rect = anchorRef.current.getBoundingClientRect();
      // Place directly below the input. If there's no room below, place above.
      const popupHeightEstimate = 240;
      const spaceBelow = window.innerHeight - rect.bottom;
      const placeAbove = spaceBelow < popupHeightEstimate && rect.top > popupHeightEstimate;
      setPos({
        top: placeAbove ? rect.top - 8 : rect.bottom + 8,
        left: Math.max(8, Math.min(rect.left - 80, window.innerWidth - 290)),
        placeAbove,
      });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [anchorRef]);

  // Close on Escape.
  //
  // On `window` in capture, and CLAIMED with `preventDefault()` (#900): the pad
  // opens inside the KM score dialog, and an Escape meant for the pad must not
  // also close the dialog — and the scores typed into it. `Dialog` listens on
  // `document` in capture and leaves an already-claimed Escape alone; this is
  // the one place that runs before it. Where there is no dialog, nothing reads
  // the flag, and the pad closes exactly as it did.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      onClose();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  // Close on a click outside the popup (but not when clicking the anchor itself).
  const popupRef = useRef(null);
  useEffect(() => {
    const onDown = (e) => {
      if (popupRef.current && popupRef.current.contains(e.target)) return;
      if (anchorRef?.current && anchorRef.current.contains(e.target)) return;
      onClose();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
    };
  }, [onClose, anchorRef]);

  // Keyboard focus into the pad, once it is placed (it renders nothing before).
  const placed = pos != null;
  useEffect(() => {
    if (!focusInside || !placed || !popupRef.current) return;
    const root = popupRef.current as HTMLElement;
    const want =
      value != null && value !== ''
        ? root.querySelector<HTMLElement>(`[data-pad-value="${String(value)}"]`)
        : null;
    (want ?? root.querySelector<HTMLElement>('[data-pad-value]'))?.focus();
  }, [focusInside, placed, value]);

  if (!pos) return null;

  // Generate all numbers from 0 to maxValue (inclusive)
  const numbers = [];
  for (let i = 0; i <= maxValue; i++) numbers.push(i);

  // 6 columns give a compact layout: 0-5 first row, 6-11 second, etc.
  // For small maxValue (e.g. bingo with 5 games) it becomes a single row.
  const cols = Math.min(6, Math.max(4, maxValue + 1));

  // NOT a `Dialog` (#900): the pad is a popover beside its field, and Tab must
  // still walk the form (#943), so it neither traps focus nor locks the page.
  // Its outer box only CATCHES events — it stops clicks reaching the row it
  // opens over, and keeps a press on its padding from blurring the field — so
  // it is `role="presentation"`, the pattern jsx-a11y documents for a
  // container that handles its children's bubbled events. The numbers are a
  // group named by the pad's «Velg tall» heading.
  return (
    <div
      ref={popupRef}
      role="presentation"
      data-number-pad=""
      onBlur={
        closeOnFocusLeave
          ? (e) => {
              const to = e.relatedTarget as Node | null;
              if (!to) return;
              if (popupRef.current?.contains(to)) return;
              if (anchorRef?.current && anchorRef.current.contains(to)) return;
              onClose();
            }
          : undefined
      }
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.preventDefault()} // prevents blur on the input
      className="fixed z-[60] bg-paper rounded-xl shadow-lg border border-line p-2"
      style={{
        top: pos.placeAbove ? 'auto' : pos.top,
        bottom: pos.placeAbove ? window.innerHeight - pos.top : 'auto',
        left: pos.left,
        maxWidth: 290,
      }}
    >
      <div className="flex items-center justify-between px-2 py-1 mb-1">
        <span id={headingId} className="font-mono text-[10px] uppercase tracking-wider text-ink-3">
          Velg tall
        </span>
        <button
          type="button"
          onClick={onClose}
          className="text-ink-3 hover:text-ink-1 text-xs leading-none p-1"
          title="Lukk"
          // The glyph is the button's only content, so it was its name: «✕».
          aria-label={t('ui.dialog.close')}
        >
          ✕
        </button>
      </div>
      <div
        role="group"
        aria-labelledby={headingId}
        className="grid gap-1"
        style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}
      >
        {numbers.map((n) => (
          <button
            key={n}
            type="button"
            data-pad-value={n}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onPick(n)}
            className="font-mono font-bold text-sm rounded-lg border border-line bg-paper hover:bg-lime-bg hover:border-lime-deep transition-colors flex items-center justify-center"
            style={{ width: 40, height: 40 }}
          >
            {n}
          </button>
        ))}
      </div>
    </div>
  );
}
